import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../../context/DarkModeContext';
import { CareerFocusWithMetrics } from '../../hooks/useCareerFocus';
import { IoArrowForwardOutline } from 'react-icons/io5';
import { motion } from 'framer-motion';
import { useRubric } from '../../hooks/useRubric';
import { supabase } from '../../utils/supabase';
import { InternshipPhase } from './InternshipDashboardV2';
import PhaseDetailsModal from './PhaseDetailsModal';
import { usePhaseGuidanceFromContext } from '../../context/PhaseGuidanceContext';
import { useInternshipPhase } from '../../hooks/useInternshipPhase';
import { createLogger } from '../../utils/logger';
import { useVisualizationConfig } from '../../hooks/useVisualizationConfig';

const logger = createLogger({ module: 'ProgressWidgets' });

interface ProgressWidgetsProps {
  focus: CareerFocusWithMetrics;
  goals: any[]; // Will use proper type from useGoals
  goalsLoading: boolean;
  cards: any[]; // Will use proper type from useCareerCards
  cardsLoading: boolean;
  activeRubric: any; // Will use proper type from useRubric
  rubricLoading: boolean;
}

interface CoverageData {
  [categoryId: string]: {
    name: string;
    total: number;
    documented: number;
    percentage: number;
  };
}

const ProgressWidgets: React.FC<ProgressWidgetsProps> = ({ 
  focus, 
  goals,
  goalsLoading,
  cards,
  cardsLoading,
  activeRubric,
  rubricLoading
}) => {
  const { darkMode } = useDarkMode();
  const [coverageData, setCoverageData] = useState<CoverageData>({});
  const [weeklyStats, setWeeklyStats] = useState({ cards: 0, notes: 0 });
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showPhaseModal, setShowPhaseModal] = useState(false);
  
  // Use visualization configuration hook
  const { getCoverageColor } = useVisualizationConfig(focus.id);
  
  // Calculate derived data
  const evidenceCount = cards?.length || 0;
  const goalsProgress = {
    total: goals?.length || 0,
    withEvidence: goals?.filter(g => (g.evidence_count || 0) > 0).length || 0
  };

  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';

  // Use centralized phase calculation hook
  const { currentPhase } = useInternshipPhase(focus);

  // Calculate coverage from rubric evidence
  useEffect(() => {
    if (!activeRubric || !focus.id) {
      // No rubric available, set empty coverage data
      setCoverageData({});
      return;
    }

    const calculateCoverage = async () => {
      // Fetch actual evidence counts from content_links
      const { data: links, error } = await supabase
        .from('content_links')
        .select('target_id, target_type, metadata')
        .eq('target_type', 'rubric_criterion')
        .in('source_type', ['career_card', 'note']);

      if (error) {
        logger.error('Error fetching evidence links:', error);
        return;
      }

      const coverage: CoverageData = {};
      
      // Count evidence for each category
      activeRubric.categories.forEach(category => {
        const totalItems = category.criteria_items?.length || category.criteria.length;
        let documentedCount = 0;

        // Check each criterion for evidence
        if (category.criteria_items && category.criteria_items.length > 0) {
          // Normalized criteria format
          category.criteria_items.forEach(criterion => {
            const hasEvidence = links?.some(link => link.target_id === criterion.id);
            if (hasEvidence) documentedCount++;
          });
        } else {
          // Legacy format - no evidence will be found (migration complete)
          documentedCount = 0;
        }
        
        coverage[category.id] = {
          name: category.name,
          total: totalItems,
          documented: documentedCount,
          percentage: totalItems > 0 ? Math.round((documentedCount / totalItems) * 100) : 0
        };
      });

      setCoverageData(coverage);
    };

    calculateCoverage();
  }, [activeRubric, focus.id, evidenceCount]);

  // Calculate weekly stats
  useEffect(() => {
    const fetchWeeklyStats = async () => {
      const weekStart = new Date();
      weekStart.setDate(weekStart.getDate() - 7);
      
      try {
        // Fetch career cards
        const { data: cards, error: cardsError } = await supabase
          .from('career_cards')
          .select('id, card_data')
          .eq('user_id', focus.user_id)
          .gte('created_at', weekStart.toISOString());

        if (cardsError) {
          logger.error('Error fetching weekly cards:', cardsError);
        }

        // Fetch notes
        const { data: notes, error: notesError } = await supabase
          .from('notes')
          .select('id')
          .eq('user_id', focus.user_id)
          .gte('created_at', weekStart.toISOString());

        if (notesError) {
          logger.error('Error fetching weekly notes:', notesError);
        }

        // Filter out archived cards (those with 'Archived' tag)
        const activeCards = cards?.filter(card => {
          const tags = card.card_data?.custom_tags || [];
          return !tags.includes('Archived');
        }) || [];

        setWeeklyStats({
          cards: activeCards.length,
          notes: notes?.length || 0
        });
      } catch (err) {
        logger.error('Failed to fetch weekly stats:', err);
        setWeeklyStats({ cards: 0, notes: 0 });
      }
    };

    if (focus.user_id) {
      fetchWeeklyStats();
    }
  }, [focus.user_id]);


  // Get categories to display based on showAllCategories state
  const allCategories = Object.entries(coverageData);
  const displayCategories = showAllCategories ? allCategories : allCategories.slice(0, 3);
  
  // Determine grid layout based on number of categories
  const getGridClass = () => {
    if (!showAllCategories || allCategories.length <= 3) return 'grid-cols-3';
    if (allCategories.length === 4) return 'grid-cols-2';
    if (allCategories.length <= 6) return 'grid-cols-3';
    return 'grid-cols-4'; // 7+ categories
  };
  
  // Determine if we should show compact mode (hide text)
  const isCompact = showAllCategories && allCategories.length > 6;

  // Fetch phase guidance for the current phase from context
  const { guidance: phaseGuidance } = usePhaseGuidanceFromContext(currentPhase);
  
  // Create phase insight text from the first focus area and tip
  const getPhaseInsight = () => {
    if (!phaseGuidance) {
      // Fallback text if guidance hasn't loaded
      return "Loading personalized guidance for your current phase...";
    }
    
    const firstTip = phaseGuidance.tips[0];
    const firstFocusArea = phaseGuidance.focus_areas[0];
    
    if (firstTip && firstFocusArea) {
      return `${firstTip.description} Focus on ${firstFocusArea.toLowerCase()} during this phase.`;
    }
    
    return "Stay focused on your goals and track your progress regularly.";
  };
  
  return (
    <div className="progress-widgets grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
      {/* Coverage Heatmap */}
      <motion.div 
        className={`${bgColor} rounded-xl border ${borderColor} p-5`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-base font-medium ${textColor}`}>Coverage Heatmap</h3>
          <button 
            onClick={() => setShowAllCategories(!showAllCategories)}
            className={`text-sm ${mutedTextColor} hover:text-blue-500 transition-colors flex items-center gap-1`}
            type="button"
          >
            {showAllCategories && allCategories.length > 3 ? 'Show less' : 'Show all'}
            <IoArrowForwardOutline className={`w-3 h-3 transition-transform ${showAllCategories ? 'rotate-90' : ''}`} />
          </button>
        </div>
        
        <div className={`grid ${getGridClass()} gap-2`}>
          {displayCategories.map(([categoryId, data]) => (
            <motion.div 
              key={categoryId} 
              className="text-center group relative"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
            >
              <div 
                className={`${getCoverageColor(data.percentage)} text-white rounded ${isCompact ? 'p-2' : 'p-4'} mb-1 relative overflow-hidden`}
                title={`${data.name}: ${data.documented}/${data.total} documented`}
              >
                {!isCompact && <div className="text-xs mb-1">{data.name.split(' ')[0]}</div>}
                <div className={`${isCompact ? 'text-sm' : 'text-lg'} font-semibold`}>{data.percentage}%</div>
                
                {/* Hover tooltip for compact mode */}
                {isCompact && (
                  <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-80 transition-all duration-200 flex items-center justify-center opacity-0 hover:opacity-100">
                    <div className="text-xs text-white p-2 text-center">
                      <div className="font-medium">{data.name}</div>
                      <div>{data.documented}/{data.total}</div>
                    </div>
                  </div>
                )}
              </div>
              {!isCompact && (
                <p className={`text-xs ${mutedTextColor}`}>
                  {data.documented}/{data.total}
                </p>
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Weekly Summary */}
      <motion.div 
        className={`${bgColor} rounded-xl border ${borderColor} p-5`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <div className="mb-4">
          <h3 className={`text-base font-medium ${textColor}`}>Weekly Summary</h3>
        </div>
        
        <div className="space-y-2 text-sm">
          {weeklyStats.cards > 0 && (
            <div className={`flex items-center gap-2 ${textColor}`}>
              <span>✅</span>
              <span>Added {weeklyStats.cards} career card{weeklyStats.cards !== 1 ? 's' : ''}</span>
            </div>
          )}
          {weeklyStats.notes > 0 && (
            <div className={`flex items-center gap-2 ${textColor}`}>
              <span>📝</span>
              <span>Created {weeklyStats.notes} note{weeklyStats.notes !== 1 ? 's' : ''}</span>
            </div>
          )}
          {goalsProgress.withEvidence > 0 && (
            <div className={`flex items-center gap-2 ${textColor}`}>
              <span>🎯</span>
              <span>Progressed on {goalsProgress.withEvidence} goal{goalsProgress.withEvidence !== 1 ? 's' : ''}</span>
            </div>
          )}
          {Object.values(coverageData).some(cat => cat.percentage === 0) && (
            <div className="flex items-center gap-2 text-yellow-500">
              <span>⚠️</span>
              <span>
                {Object.values(coverageData).find(cat => cat.percentage === 0)?.name} needs evidence
              </span>
            </div>
          )}
          {/* Show encouraging message if no activity */}
          {weeklyStats.cards === 0 && weeklyStats.notes === 0 && goalsProgress.withEvidence === 0 && (
            <div className={`flex items-center gap-2 ${mutedTextColor}`}>
              <span>💡</span>
              <span>Start documenting your work to track progress</span>
            </div>
          )}
        </div>
      </motion.div>

      {/* Phase Insights */}
      <motion.div 
        className={`${bgColor} rounded-xl border ${borderColor} p-5`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-base font-medium ${textColor}`}>Phase Insights</h3>
          <button 
            type="button"
            onClick={() => setShowPhaseModal(true)}
            className={`text-sm ${mutedTextColor} hover:text-blue-500 transition-colors flex items-center gap-1`}
          >
            Learn more
            <IoArrowForwardOutline className="w-3 h-3" />
          </button>
        </div>
        
        <p className={`text-sm ${mutedTextColor} leading-relaxed`}>
          {getPhaseInsight()}
        </p>
      </motion.div>

      {/* Phase Details Modal */}
      <PhaseDetailsModal
        isOpen={showPhaseModal}
        onClose={() => setShowPhaseModal(false)}
        currentPhase={currentPhase}
        focus={focus}
      />
    </div>
  );
};

export default ProgressWidgets;